import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Lock, Download, HelpCircle } from "lucide-react";
import { toast, Toaster } from "sonner";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import StatCounter from "../components/StatCounter";
import RequestPitchDeckModal from "../components/RequestPitchDeckModal";
import { IMAGES } from "../lib/images";
import { api, formatApiErrorDetail } from "../lib/api";

const PHILOSOPHY = [
  { n: "01", t: "Long Duration Capital", b: "Generational assets demand generational thinking. We partner with capital that prioritizes legacy over liquidity." },
  { n: "02", t: "Institutional Execution", b: "Every project is delivered with Fortune-500 development discipline — design, capital structure, and construction." },
  { n: "03", t: "Market Conviction", b: "Tampa is the Gulf Coast's most under-supplied luxury market. We are early — and decisive." },
  { n: "04", t: "Aligned Outcomes", b: "Our capital structure aligns sponsor and LP returns across the full asset lifecycle." },
];

const FAQ = [
  { q: "What investment vehicles does EID offer?", a: "EID structures co-GP, LP, and preferred equity opportunities depending on the asset and capital partner profile. Confidential briefings available upon qualified inquiry." },
  { q: "What is the typical minimum?", a: "Institutional partnerships generally begin at $5M. Family offices and individual accredited investors may participate at lower commitments through select syndications." },
  { q: "What returns does EID target?", a: "Targeted returns vary by project and structure — currently targeting approximately 18% IRR with a 2.0x equity multiple on the flagship Elliott Towers development. Returns are not guaranteed and depend on market, financing, and execution outcomes." },
  { q: "How do I access the investor portal?", a: "Qualified investors may request portal access through our investor relations team. A demo investor account is available for review purposes." },
];

export default function InvestorRelationsPage() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    investor_type: "institutional", investment_range: "5m_25m",
    project_interest: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/investor/inquiry", form);
      toast.success("Inquiry received. Investor relations will be in touch.");
      setForm((f) => ({ ...f, message: "" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO title="Investor Relations" description="Institutional capital partnerships with Elliott International Development — pipeline, philosophy, opportunities, and confidential investor portal access." path="/investor-relations" />
      <Toaster theme="dark" position="bottom-right" />
      <RequestPitchDeckModal open={deckOpen} onClose={() => setDeckOpen(false)} />

      {/* HERO */}
      <section className="relative pt-40 pb-20 px-6 lg:px-12 overflow-hidden">
        <div className="absolute right-0 top-32 w-1/2 h-[500px] opacity-30">
          <img src={IMAGES.architectural_close_up} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-background" />
        </div>
        <div className="relative max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline">— Investor Relations</div>
            <div className="overline mt-2 text-foreground/50">Institutional · Family Office · Strategic</div>
          </div>
          <div className="lg:col-span-8">
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.98] font-light tracking-tight max-w-5xl">
              Capital partnerships of <em className="text-primary italic">institutional</em> calibre.
            </motion.h1>
            <p className="mt-8 text-muted-foreground text-lg max-w-2xl leading-relaxed">
              EID partners with select capital to deliver landmark mixed-use
              developments — engineered for generational outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* OPPORTUNITY METRICS */}
      <section id="opportunities" className="border-y border-primary/10 py-20 px-6 lg:px-12 bg-card">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-10">
          <StatCounter value={1} prefix="$" suffix="B+" label="Total Pipeline" />
          <StatCounter value={1} suffix=".3M+ SF" label="Under Vision" />
          <StatCounter value={18} suffix="% target" label="IRR" />
          <StatCounter value={2} suffix=".0x target" label="Equity Multiple" />
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-32 lg:py-44 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          <SectionHeading overline="— Investment philosophy" title="Discipline. Conviction. Legacy." />
          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PHILOSOPHY.map((p, i) => (
              <motion.div key={p.n} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }} className="glass p-8">
                <div className="font-mono text-primary text-sm tracking-widest">{p.n}</div>
                <h3 className="mt-6 font-serif text-2xl tracking-tight">{p.t}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURE PORTAL + DECK */}
      <section className="py-32 px-6 lg:px-12 bg-card border-y border-primary/10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="glass p-12 flex flex-col justify-between">
            <div>
              <Lock className="w-7 h-7 text-primary mb-6" />
              <div className="overline mb-4">— Secure investor portal</div>
              <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                Confidential project data, capital structure, and reporting — for verified partners.
              </h3>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                Qualified investors gain access to project briefings, financial
                models, construction milestones, and quarterly reporting.
              </p>
            </div>
            <Link to="/login" data-testid="ir-portal-cta" className="mt-8 group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors w-fit">
              Access Investor Portal <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="glass p-12 flex flex-col justify-between">
            <div>
              <Download className="w-7 h-7 text-primary mb-6" />
              <div className="overline mb-4">— Investor materials</div>
              <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                Pitch deck, project briefings, and confidential financial models.
              </h3>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                Materials are available upon qualified inquiry. Submit a request
                and our team will deliver a confidential package.
              </p>
            </div>
            <button data-testid="ir-deck-cta" onClick={() => setDeckOpen(true)} className="mt-8 group inline-flex items-center gap-3 border border-primary/40 text-primary px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary hover:text-primary-foreground transition-colors w-fit">
              Request Pitch Deck <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section className="py-32 lg:py-44 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-6">— Investor inquiry</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              Begin a confidential conversation.
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Submit a confidential inquiry and our investor relations team will
              respond within two business days.
            </p>
            <div className="mt-12 space-y-4 text-sm">
              <div><div className="overline text-foreground/50">Direct</div><div className="font-mono text-primary mt-1">investors@elliottdev.com</div></div>
              <div><div className="overline text-foreground/50">Tampa Office</div><div className="font-mono mt-1">+1 (703) 489-2129</div></div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <form onSubmit={submit} data-testid="ir-investor-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input data-testid="ir-input-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
              <input data-testid="ir-input-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
              <input data-testid="ir-input-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
              <input data-testid="ir-input-company" placeholder="Firm / Family Office" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
              <select data-testid="ir-input-investor-type" value={form.investor_type} onChange={(e) => setForm({ ...form, investor_type: e.target.value })} className="bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors">
                <option value="individual">Individual / Accredited</option>
                <option value="institutional">Institutional Fund</option>
                <option value="family_office">Family Office</option>
                <option value="fund">REIT / Private Fund</option>
                <option value="developer">Developer / Strategic</option>
              </select>
              <select data-testid="ir-input-investment-range" value={form.investment_range} onChange={(e) => setForm({ ...form, investment_range: e.target.value })} className="bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors">
                <option value="under_1m">Under $1M</option>
                <option value="1m_5m">$1M – $5M</option>
                <option value="5m_25m">$5M – $25M</option>
                <option value="25m_100m">$25M – $100M</option>
                <option value="100m_plus">$100M+</option>
              </select>
              <input data-testid="ir-input-project" placeholder="Project of interest (optional)" value={form.project_interest} onChange={(e) => setForm({ ...form, project_interest: e.target.value })} className="md:col-span-2 bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
              <textarea data-testid="ir-input-message" required placeholder="How can we assist?" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="md:col-span-2 bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors resize-none" />
              <button data-testid="ir-submit-investor" disabled={submitting} type="submit" className="md:col-span-2 group inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-all disabled:opacity-60">
                {submitting ? "Submitting…" : "Submit Confidential Inquiry"} <ArrowUpRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-32 px-6 lg:px-12 bg-card border-t border-primary/10">
        <div className="max-w-[1200px] mx-auto">
          <SectionHeading overline="— FAQ" title="Common questions." />
          <div className="mt-16 space-y-2">
            {FAQ.map((f) => (
              <details key={f.q} data-testid={`faq-item-${f.q.slice(0, 24)}`} className="group border-t border-primary/10 py-6">
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                  <h3 className="font-serif text-xl md:text-2xl">{f.q}</h3>
                  <HelpCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0 transition-transform group-open:rotate-45" />
                </summary>
                <p className="mt-4 text-muted-foreground leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
